import { createContext, useCallback, useEffect, useReducer } from 'react';
import axios from '../utils/axios';
import { isValidToken, setSession } from '../utils/jwt';
import { getLoggedUser } from '../services/authServices';

export interface User {
  id: string;
  email: string;
  is_active: boolean;
  is_superuser: boolean;
  is_verified: boolean;
}

interface State {
  isAuthenticated: boolean;
  isInitialized: boolean;
  user: User | null;
}

interface Action {
  type: string;
  payload?: any;
}

export interface LoginFunc {
  (email: string, password: string): Promise<{ success: boolean; error: any }>;
}

interface TokenAuthentication {
  access_token: string;
  multi_account_tokens?: unknown;
}

interface AuthContextProps {
  isAuthenticated: boolean;
  isInitialized: boolean;
  user: User | null;
  method: string;
  login: LoginFunc;
  loginWithToken: (authentication: TokenAuthentication) => Promise<void>;
  logout: () => Promise<void>;
  register: (data: RegisterData) => Promise<{ success: boolean; error: any }>;
  resetPassword: (email: string) => void;
  updateProfile: () => void;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

interface RegisterData {
  email: string;
  password: string;
}

interface Handlers {
  [key: string]: (state: State, action: Action) => State;
  INITIALIZE: (state: State, action: Action) => State;
  LOGIN: (state: State, action: Action) => State;
  LOGOUT: (state: State) => State;
  REGISTER: (state: State, action: Action) => State;
}

const initialState: State = {
  isAuthenticated: false,
  isInitialized: false,
  user: null
};

const handlers: Handlers = {
  INITIALIZE: (state: State, action: Action) => {
    const { isAuthenticated, user } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user
    };
  },
  LOGIN: (state: State, action: Action) => {
    const { user } = action.payload;
    return {
      ...state,
      isAuthenticated: true,
      user
    };
  },
  LOGOUT: (state: State) => ({
    ...state,
    isAuthenticated: false,
    user: null
  }),
  REGISTER: (state: State, action: Action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user
    };
  }
};

const reducer = (state: State, action: Action) =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

const AuthContext = createContext<AuthContextProps>({
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  method: 'jwt',
  login: async (email: string, password: string) => ({ success: false, error: null }),
  loginWithToken: async () => {
    throw new Error('Authentication is unavailable. Please reload and try again.');
  },
  logout: async () => {},
  register: async (data: RegisterData) => ({ success: false, error: null }),
  resetPassword: async (email: string) => {},
  updateProfile: () => {}
});

function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const initialize = async () => {
      try {
        const accessToken = window.localStorage.getItem('accessToken');
        if (accessToken && isValidToken(accessToken)) {
          setSession(accessToken);
          const response = await getLoggedUser();
          const email = response.data?.email;
          if (email) {
            localStorage.setItem('email', email);
          }
          const userId = response.data?.user_id;
          if (userId) {
            localStorage.setItem('userId', userId);
          }
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: true,
              user: response.data
            }
          });
        } else {
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: false,
              user: null
            }
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null
          }
        });
      }
    };

    initialize();
  }, []);

  const loginWithToken = useCallback(async (authentication: TokenAuthentication) => {
    if (!authentication.access_token || !isValidToken(authentication.access_token)) {
      throw new Error('The sign-in session is invalid. Please try again.');
    }

    // Load the new identity before replacing any existing Vennie session.
    const response = await axios.get('/users/me', {
      headers: { Authorization: `Bearer ${authentication.access_token}` }
    });
    const user = response.data;
    const userId = user?.user_id || user?.id;
    if (!user?.email || !userId) {
      throw new Error('Unable to load your account. Please try again.');
    }

    setSession(authentication.access_token);
    localStorage.setItem('email', user.email);
    localStorage.setItem('userId', userId);
    if (authentication.multi_account_tokens) {
      localStorage.setItem('multi_account_tokens', JSON.stringify(authentication.multi_account_tokens));
    } else {
      localStorage.removeItem('multi_account_tokens');
    }
    dispatch({ type: 'LOGIN', payload: { user } });
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const params = new URLSearchParams();
      params.append('username', email);
      params.append('password', password);
      const response = await axios.post(`${process.env.REACT_APP_BASE_API}/auth/jwt/login`, params);
      setSession(response.data.access_token);
      const user = await axios.get(`${process.env.REACT_APP_BASE_API}/users/me`);
      const emailAddress = user.data?.email;
      if (emailAddress) {
        localStorage.setItem('email', emailAddress);
      }
      const userId = user.data?.user_id;
      if (userId) {
        localStorage.setItem('userId', userId);
      }

      if (user.data && response.data.access_token) {
        
        localStorage.setItem('accessToken', response.data.access_token);
        if (response.data.multi_account_tokens) {
          localStorage.setItem('multi_account_tokens', JSON.stringify(response.data.multi_account_tokens));
        }
        
        dispatch({
          type: 'LOGIN',
          payload: {
            user: {
              email,
              username: user.data
            }
          }
        });

        return {
          success: true,
          error: null
        };
      } else {
        return {
          success: false,
          error: user.data.error || response.data.error || response.statusText
        };
      }
    } catch (err) {
      console.log(err);

      return {
        success: false,
        error: err
      };
    }
  };

  const register = async (data: RegisterData) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_BASE_API}/auth/register`, {
        email: data.email,
        password: data.password
      });

      if (response.status === 201) {
        return await login(data.email, data.password);
      }

      return {
        success: false,
        error: response.data.detail || response.data.details || response.data.error
      };
    } catch (err) {
      return {
        success: false,
        error: err
      };
    }
  };

  const logout = async () => {
    setSession(null);
    localStorage.removeItem('multi_account_tokens');
    localStorage.removeItem('userId');
    dispatch({ type: 'LOGOUT' });
  };

  const resetPassword = async (email: string) => {};

  const updateProfile = () => {};

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        login,
        loginWithToken,
        logout,
        register,
        resetPassword,
        updateProfile
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
